import { Search, SlidersHorizontal, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button, Checkbox } from 'src/shared/components/ui'
import { Input } from 'src/shared/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/shared/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/shared/components/ui/select'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { SearchField, SearchFilters } from '../types'

interface UnifiedFilterBarProps {
  searchFilters: SearchFilters
  onSearchChange: (filters: SearchFilters) => void
  showKlayderRecommendedProducts: boolean
  onKlayderRecommendedChange: (checked: boolean) => void
  showWithoutCategories: boolean
  onWithoutCategoriesChange: (checked: boolean) => void
  showWithoutTranslations: boolean
  onWithoutTranslationsChange: (checked: boolean) => void
  showDraft: boolean
  onDraftChange: (checked: boolean) => void
  onClearAll: () => void
}

export const UnifiedFilterBar = ({
  searchFilters,
  onSearchChange,
  showKlayderRecommendedProducts,
  onKlayderRecommendedChange,
  showWithoutCategories,
  onWithoutCategoriesChange,
  showWithoutTranslations,
  onWithoutTranslationsChange,
  showDraft,
  onDraftChange,
  onClearAll,
}: UnifiedFilterBarProps) => {
  const { t } = useLocalization()
  const [searchField, setSearchField] = useState<SearchField>('all')
  const [searchValue, setSearchValue] = useState('')

  // Sync searchValue from URL params on mount and when field changes
  useEffect(() => {
    if (searchField === 'all') {
      // Show first non-empty value
      setSearchValue(
        searchFilters.articleNumber ||
          searchFilters.itemName ||
          searchFilters.source ||
          searchFilters.category ||
          ''
      )
    } else {
      setSearchValue(searchFilters[searchField] || '')
    }
  }, [searchField, searchFilters])

  const handleSearchChange = (value: string) => {
    setSearchValue(value)
    if (searchField === 'all') {
      // Search across all fields
      onSearchChange({
        articleNumber: value,
        itemName: value,
        source: value,
        category: value,
      })
    } else {
      // Search specific field only, clear others
      onSearchChange({
        articleNumber: searchField === 'articleNumber' ? value : '',
        itemName: searchField === 'itemName' ? value : '',
        source: searchField === 'source' ? value : '',
        category: searchField === 'category' ? value : '',
      })
    }
  }

  const hasAnyFilter =
    searchFilters.articleNumber ||
    searchFilters.itemName ||
    searchFilters.source ||
    searchFilters.category ||
    showKlayderRecommendedProducts ||
    showWithoutCategories ||
    showWithoutTranslations ||
    showDraft

  const getPlaceholder = () => {
    switch (searchField) {
      case 'articleNumber':
        return t('Search article number...', 'Sök artikelnummer...')
      case 'itemName':
        return t('Search name...', 'Sök namn...')
      case 'source':
        return t('Search source...', 'Sök källa...')
      case 'category':
        return t('Search category...', 'Sök kategori...')
      default:
        return t('Search products...', 'Sök produkter...')
    }
  }

  return (
    <Column className="space-y-2 pb-2">
      <Row className="gap-2 items-center flex-wrap">
        {/* Search input */}
        <Input
          startContent={<Search className="h-4 w-4 text-muted-foreground" />}
          placeholder={getPlaceholder()}
          value={searchValue}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="flex-1 min-w-[200px] max-w-[350px]"
        />

        {/* Field selector */}
        <Select
          value={searchField}
          onValueChange={(val) => setSearchField(val as SearchField)}
        >
          <SelectTrigger className="w-[130px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="articleNumber">
              {t('Article no.', 'Art.nr')}
            </SelectItem>
            <SelectItem value="itemName">{t('Name', 'Namn')}</SelectItem>
            <SelectItem value="source">{t('Source', 'Källa')}</SelectItem>
            <SelectItem value="category">
              {t('Category', 'Kategori')}
            </SelectItem>
          </SelectContent>
        </Select>

        {/* More filters popover */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="secondary"
              className={
                showKlayderRecommendedProducts ||
                showWithoutCategories ||
                showWithoutTranslations ||
                showDraft
                  ? 'ring-2 ring-purple-500 border-purple-300'
                  : ''
              }
            >
              <SlidersHorizontal className="h-4 w-4 mr-2" />
              {t('More', 'Mer')}
            </Button>
          </PopoverTrigger>

          <PopoverContent className="w-[220px]" align="start">
            <Column className="gap-3">
              <Checkbox
                label="Klayder recommends"
                checked={showKlayderRecommendedProducts}
                setChecked={onKlayderRecommendedChange}
              />
              <Checkbox
                label={t('Without categories', 'Utan kategorier')}
                checked={showWithoutCategories}
                setChecked={onWithoutCategoriesChange}
              />
              <Checkbox
                label={t('Without translations', 'Utan översättningar')}
                checked={showWithoutTranslations}
                setChecked={onWithoutTranslationsChange}
              />
              <Checkbox
                label={t('Draft only', 'Endast utkast')}
                checked={showDraft}
                setChecked={onDraftChange}
              />
            </Column>
          </PopoverContent>
        </Popover>

        {/* Clear all - only when filters active */}
        {hasAnyFilter && (
          <Button
            variant="ghost"
            onClick={onClearAll}
            className="text-muted-foreground"
          >
            <X className="h-4 w-4 mr-1" />
            {t('Clear', 'Rensa')}
          </Button>
        )}
      </Row>
    </Column>
  )
}
